import { useState, useMemo } from 'react';
import { Plus, X, Trash2, Search, ChevronLeft, BarChart3, Globe, Lock, Star, Copy, Share2 } from 'lucide-react';
import { CardImage } from '@/components/CardImage';
import { InkDot } from '@/components/InkDot';
import { RarityBadge } from '@/components/RarityBadge';
import { LegalityPanel } from '@/components/LegalityPanel';
import { DeckSuggestions } from '@/components/DeckSuggestions';
import { DeckExportSheet } from '@/components/DeckExportSheet';
import { useDecks, useDeckCards, useCreateDeck, useDeleteDeck, useAddCardToDeck, useRemoveCardFromDeck } from '@/hooks/useDecks';
import { usePublicDecks, useTogglePublish, useRateDeck, useCopyDeck } from '@/hooks/useDeckSharing';
import { useCards, type Card } from '@/hooks/useCards';
import { useAuth } from '@/hooks/useAuth';
import { validateDeck } from '@/lib/deck-utils';
import type { InkColor } from '@/lib/ink-utils';
import { toast } from 'sonner';

const INK_OPTIONS: InkColor[] = ['Amber', 'Amethyst', 'Emerald', 'Ruby', 'Sapphire', 'Steel'];

export function DeckBuilderTab() {
  const { user } = useAuth();
  const { data: decks, isLoading } = useDecks();
  const createDeck = useCreateDeck();
  const deleteDeck = useDeleteDeck();
  const togglePublish = useTogglePublish();

  const [viewMode, setViewMode] = useState<'my' | 'public'>('my');
  const [activeDeckId, setActiveDeckId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newInk1, setNewInk1] = useState<InkColor>('Amber');
  const [newInk2, setNewInk2] = useState<InkColor>('Amethyst');

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const deck = await createDeck.mutateAsync({ name: newName, ink_1: newInk1, ink_2: newInk2 });
      toast.success('Deck created!');
      setShowCreate(false);
      setNewName('');
      setActiveDeckId(deck.id);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDeck.mutateAsync(id);
      toast.success('Deck deleted');
      if (activeDeckId === id) setActiveDeckId(null);
    } catch (err: any) { toast.error(err.message); }
  };

  if (activeDeckId) {
    return <DeckEditor deckId={activeDeckId} onBack={() => setActiveDeckId(null)} />;
  }

  if (viewMode === 'public') {
    return <PublicDeckGallery onSwitchToMy={() => setViewMode('my')} />;
  }

  return (
    <div className="px-4 pb-4">
      {/* View toggle */}
      <div className="flex gap-1 rounded-xl bg-secondary p-1 mt-3 mb-3">
        <button className="flex-1 rounded-lg py-2 text-xs font-medium font-body bg-card text-foreground shadow-sm">My Decks</button>
        <button onClick={() => setViewMode('public')} className="flex-1 rounded-lg py-2 text-xs font-medium font-body text-muted-foreground">Public Decks</button>
      </div>

      {/* Create button */}
      <button
        onClick={() => setShowCreate(true)}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 py-4 text-sm font-medium text-primary font-body hover:border-primary/60 transition-colors"
      >
        <Plus className="h-5 w-5" /> New Deck
      </button>

      {/* Deck list */}
      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground font-body text-sm">Loading...</div>
      ) : !decks?.length ? (
        <div className="py-16 text-center">
          <p className="text-4xl mb-3">🃏</p>
          <p className="text-sm font-medium text-foreground font-body mb-1">No decks yet</p>
          <p className="text-xs text-muted-foreground font-body">Create your first 60-card deck to start building!</p>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {decks.map(deck => (
            <div key={deck.id} className="rounded-xl border border-border bg-card p-4 animate-fade-in">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-sm font-semibold text-foreground">{deck.name}</h3>
                <button onClick={() => handleDelete(deck.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <InkDot ink={deck.ink_1 as InkColor} size="md" />
                <InkDot ink={deck.ink_2 as InkColor} size="md" />
                <span className="text-xs text-muted-foreground font-body">{deck.ink_1} / {deck.ink_2}</span>
                <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-medium font-body ${deck.total_cards === 60 ? 'bg-ink-emerald/20 text-ink-emerald' : 'bg-secondary text-muted-foreground'}`}>
                  {deck.total_cards}/60 cards
                </span>
              </div>
              {deck.description && <p className="text-xs text-muted-foreground font-body mb-3">{deck.description}</p>}
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveDeckId(deck.id)}
                  className="flex-1 rounded-lg bg-primary py-2.5 text-xs font-medium text-primary-foreground font-body"
                >
                  Edit Deck
                </button>
                <button
                  onClick={async () => {
                    try {
                      await togglePublish.mutateAsync({ deckId: deck.id, published: !(deck as any).published });
                      toast.success((deck as any).published ? 'Deck unpublished' : 'Deck published!');
                    } catch (err: any) { toast.error(err.message); }
                  }}
                  className={`flex items-center gap-1 rounded-lg px-3 py-2.5 text-xs font-medium font-body transition-colors ${(deck as any).published ? 'bg-ink-emerald/10 text-ink-emerald border border-ink-emerald/30' : 'bg-secondary text-muted-foreground'}`}
                >
                  {(deck as any).published ? <><Globe className="h-3 w-3" /> Public</> : <><Lock className="h-3 w-3" /> Private</>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
          <div className="animate-slide-up w-full max-w-[430px] rounded-t-2xl border border-border bg-card p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-foreground">New Deck</h3>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Deck name" className="w-full rounded-lg border border-border bg-secondary px-3 py-2.5 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary mb-3" />
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Ink 1</p>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {INK_OPTIONS.map(ink => (
                <button key={ink} onClick={() => setNewInk1(ink)} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${newInk1 === ink ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  <InkDot ink={ink} size="sm" />{ink}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground font-body mb-1.5 uppercase tracking-wider">Ink 2</p>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {INK_OPTIONS.map(ink => (
                <button key={ink} onClick={() => setNewInk2(ink)} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium font-body transition-colors ${newInk2 === ink ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                  <InkDot ink={ink} size="sm" />{ink}
                </button>
              ))}
            </div>
            <button onClick={handleCreate} disabled={!newName.trim() || createDeck.isPending} className="w-full rounded-lg bg-primary py-3 text-sm font-medium text-primary-foreground font-body disabled:opacity-50">
              {createDeck.isPending ? 'Creating...' : 'Create Deck'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Deck Editor ────────────────────────────────────────────
function DeckEditor({ deckId, onBack }: { deckId: string; onBack: () => void }) {
  const { data: deckCards, isLoading } = useDeckCards(deckId);
  const { data: decks } = useDecks();
  const addCard = useAddCardToDeck();
  const removeCard = useRemoveCardFromDeck();
  const [showCardPicker, setShowCardPicker] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showExport, setShowExport] = useState(false);

  const deck = decks?.find(d => d.id === deckId);
  const cards = deckCards || [];
  const totalCards = cards.reduce((sum, c) => sum + c.quantity, 0);

  const legality = useMemo(() => {
    if (!deck) return null;
    return validateDeck(cards, [deck.ink_1, deck.ink_2]);
  }, [cards, deck]);

  const cardIds = useMemo(() => cards.map(c => c.card_id), [cards]);

  // Ink curve analysis
  const costCurve = useMemo(() => {
    const curve: Record<number, number> = {};
    cards.forEach(dc => {
      const cost = dc.card?.cost ?? 0;
      const bucket = cost >= 7 ? 7 : cost;
      curve[bucket] = (curve[bucket] || 0) + dc.quantity;
    });
    return curve;
  }, [cards]);

  const maxCurve = Math.max(...Object.values(costCurve), 1);

  // Type breakdown
  const typeBreakdown = useMemo(() => {
    const types: Record<string, number> = {};
    cards.forEach(dc => {
      const t = dc.card?.card_type || 'Unknown';
      types[t] = (types[t] || 0) + dc.quantity;
    });
    return types;
  }, [cards]);

  // Ink breakdown
  const inkBreakdown = useMemo(() => {
    const inks: Record<string, number> = {};
    cards.forEach(dc => {
      const ink = dc.card?.ink_color || 'Unknown';
      inks[ink] = (inks[ink] || 0) + dc.quantity;
    });
    return inks;
  }, [cards]);

  const inkableCount = cards.reduce((sum, dc) => sum + (dc.card?.inkable ? dc.quantity : 0), 0);

  const handleAdd = async (card: Card) => {
    try {
      await addCard.mutateAsync({ deckId, cardId: card.id });
      toast.success(`Added ${card.name}`);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleRemove = async (cardId: string) => {
    try {
      await removeCard.mutateAsync({ deckId, cardId });
    } catch (err: any) { toast.error(err.message); }
  };

  return (
    <div className="px-4 pb-4">
      {/* Header */}
      <div className="mt-3 flex items-center gap-2 mb-3">
        <button onClick={onBack} className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-foreground">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-base font-semibold text-foreground truncate">{deck?.name || 'Deck'}</h2>
          <div className="flex items-center gap-2">
            {deck && <InkDot ink={deck.ink_1 as InkColor} size="sm" />}
            {deck && <InkDot ink={deck.ink_2 as InkColor} size="sm" />}
            <span className={`text-xs font-body ${totalCards === 60 ? 'text-ink-emerald' : totalCards > 60 ? 'text-ink-ruby' : 'text-muted-foreground'}`}>
              {totalCards}/60 cards
            </span>
          </div>
        </div>
        <button onClick={() => setShowStats(!showStats)} className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${showStats ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground'}`} aria-label="Toggle stats">
          <BarChart3 className="h-4 w-4" />
        </button>
        <button onClick={() => setShowExport(true)} disabled={!cards.length} className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40" aria-label="Export deck">
          <Share2 className="h-4 w-4" />
        </button>
        <button onClick={() => setShowCardPicker(true)} className="flex h-8 items-center gap-1 rounded-lg bg-primary px-3 text-xs font-medium text-primary-foreground font-body">
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>

      {/* Legality validator */}
      {legality && cards.length > 0 && <LegalityPanel report={legality} />}

      {/* Suggestions */}
      {deck && cards.length > 0 && (
        <DeckSuggestions
          deckId={deckId}
          currentCardIds={cardIds}
          allowedInks={[deck.ink_1 as InkColor, deck.ink_2 as InkColor]}
          onAdd={handleAdd}
        />
      )}

      {/* Stats panel */}
      {showStats && (
        <div className="rounded-xl border border-border bg-card p-4 mb-3 animate-fade-in">
          <h3 className="text-xs font-medium text-foreground font-body mb-3">Ink Curve</h3>
          <div className="flex items-end gap-1 h-20 mb-1">
            {[0, 1, 2, 3, 4, 5, 6, 7].map(cost => {
              const count = costCurve[cost] || 0;
              const height = maxCurve > 0 ? (count / maxCurve) * 100 : 0;
              return (
                <div key={cost} className="flex-1 flex flex-col items-center gap-0.5">
                  <span className="text-[9px] text-muted-foreground font-body">{count || ''}</span>
                  <div className="w-full rounded-t bg-primary/60" style={{ height: `${height}%`, minHeight: count > 0 ? '4px' : '0' }} />
                </div>
              );
            })}
          </div>
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4, 5, 6, '7+'].map(c => (
              <span key={c} className="flex-1 text-center text-[9px] text-muted-foreground font-body">{c}</span>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <p className="text-[10px] text-muted-foreground font-body mb-1 uppercase tracking-wider">Type</p>
              {Object.entries(typeBreakdown).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between text-xs font-body py-0.5">
                  <span className="text-foreground">{type}</span>
                  <span className="text-muted-foreground">{count}</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground font-body mb-1 uppercase tracking-wider">Ink Split</p>
              {Object.entries(inkBreakdown).map(([ink, count]) => (
                <div key={ink} className="flex items-center gap-1.5 text-xs font-body py-0.5">
                  <InkDot ink={ink as InkColor} size="sm" />
                  <span className="text-foreground">{ink}</span>
                  <span className="ml-auto text-muted-foreground">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between text-xs font-body">
            <span className="text-muted-foreground">Inkable cards</span>
            <span className={`font-medium ${inkableCount >= 20 ? 'text-ink-emerald' : 'text-ink-ruby'}`}>{inkableCount}/{totalCards}</span>
          </div>
        </div>
      )}

      {/* Card list */}
      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground font-body text-sm">Loading...</div>
      ) : cards.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-4xl mb-3">📝</p>
          <p className="text-sm text-muted-foreground font-body">Tap "Add" to start building your deck</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {cards.map(dc => {
            if (!dc.card) return null;
            return (
              <div key={dc.id} className="flex items-center gap-2.5 rounded-xl border border-border bg-card p-2 animate-fade-in">
                <CardImage src={dc.card.image_url} alt={dc.card.name} inkColor={dc.card.ink_color as InkColor} className="w-10 aspect-[2/3] rounded flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <InkDot ink={dc.card.ink_color as InkColor} size="sm" />
                    <span className="text-xs font-medium text-foreground font-body truncate">{dc.card.name}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[9px] text-muted-foreground font-body">⬡{dc.card.cost || 0}</span>
                    <RarityBadge rarity={dc.card.rarity} />
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => handleRemove(dc.card_id)} className="flex h-6 w-6 items-center justify-center rounded bg-secondary text-foreground text-xs hover:bg-accent">−</button>
                  <span className="text-xs font-medium text-foreground font-body w-4 text-center">{dc.quantity}</span>
                  <button onClick={() => handleAdd(dc.card!)} disabled={dc.quantity >= 4} className="flex h-6 w-6 items-center justify-center rounded bg-secondary text-foreground text-xs hover:bg-accent disabled:opacity-30">+</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Card picker modal */}
      {showCardPicker && deck && (
        <CardPicker
          ink1={deck.ink_1 as InkColor}
          ink2={deck.ink_2 as InkColor}
          onSelect={handleAdd}
          onClose={() => setShowCardPicker(false)}
          existingCards={cards}
        />
      )}

      {/* Export sheet */}
      {deck && (
        <DeckExportSheet
          open={showExport}
          onClose={() => setShowExport(false)}
          deckName={deck.name}
          ink1={deck.ink_1 as InkColor}
          ink2={deck.ink_2 as InkColor}
          cards={cards}
        />
      )}
    </div>
  );
}

// ─── Card Picker ────────────────────────────────────────────
function CardPicker({
  ink1, ink2, onSelect, onClose, existingCards,
}: {
  ink1: InkColor; ink2: InkColor;
  onSelect: (card: Card) => void;
  onClose: () => void;
  existingCards: { card_id: string; quantity: number }[];
}) {
  const [search, setSearch] = useState('');
  const [activeInk, setActiveInk] = useState<InkColor>(ink1);

  const { data: cards, isLoading } = useCards({
    ink_color: activeInk,
    search: search || undefined,
  });

  const existingMap = new Map(existingCards.map(c => [c.card_id, c.quantity]));

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div className="animate-slide-up w-full max-w-[430px] max-h-[85vh] rounded-t-2xl border border-border bg-card flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-base font-semibold text-foreground">Add Cards</h3>
            <button onClick={onClose} className="text-muted-foreground"><X className="h-5 w-5" /></button>
          </div>
          {/* Ink toggle */}
          <div className="flex gap-1.5 mb-3">
            {[ink1, ink2].map(ink => (
              <button key={ink} onClick={() => setActiveInk(ink)} className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-[10px] font-medium font-body transition-colors ${activeInk === ink ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}>
                <InkDot ink={ink} size="sm" />{ink}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search cards..." className="w-full rounded-lg border border-border bg-secondary py-2 pl-9 pr-3 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground font-body text-sm">Loading...</div>
          ) : (
            <div className="space-y-1.5">
              {(cards || []).map(card => {
                const qty = existingMap.get(card.id) || 0;
                return (
                  <button
                    key={card.id}
                    onClick={() => onSelect(card)}
                    disabled={qty >= 4}
                    className="flex w-full items-center gap-2.5 rounded-xl border border-border bg-secondary p-2 text-left hover:border-primary/30 transition-colors disabled:opacity-40"
                  >
                    <CardImage src={card.image_url} alt={card.name} inkColor={card.ink_color as InkColor} className="w-8 aspect-[2/3] rounded flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium text-foreground font-body truncate block">{card.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] text-muted-foreground font-body">⬡{card.cost || 0}</span>
                        <RarityBadge rarity={card.rarity} />
                      </div>
                    </div>
                    {qty > 0 && <span className="text-[10px] text-primary font-body font-medium">×{qty}</span>}
                    <Plus className="h-4 w-4 text-primary flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Public Deck Gallery ────────────────────────────────────
function PublicDeckGallery({ onSwitchToMy }: { onSwitchToMy: () => void }) {
  const { user } = useAuth();
  const { data: publicDecks, isLoading } = usePublicDecks();
  const rateDeck = useRateDeck();
  const copyDeck = useCopyDeck();

  const handleCopy = async (deckId: string) => {
    try {
      await copyDeck.mutateAsync(deckId);
      toast.success('Deck copied to your collection!');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleRate = async (deckId: string, rating: number) => {
    try {
      await rateDeck.mutateAsync({ deckId, rating });
    } catch (err: any) { toast.error(err.message); }
  };

  return (
    <div className="px-4 pb-4">
      <div className="flex gap-1 rounded-xl bg-secondary p-1 mt-3 mb-3">
        <button onClick={onSwitchToMy} className="flex-1 rounded-lg py-2 text-xs font-medium font-body text-muted-foreground">My Decks</button>
        <button className="flex-1 rounded-lg py-2 text-xs font-medium font-body bg-card text-foreground shadow-sm">Public Decks</button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground font-body text-sm">Loading...</div>
      ) : !publicDecks?.length ? (
        <div className="py-16 text-center">
          <p className="text-4xl mb-3">🌐</p>
          <p className="text-sm font-medium text-foreground font-body mb-1">No public decks yet</p>
          <p className="text-xs text-muted-foreground font-body">Be the first to publish a deck!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {publicDecks.map(deck => (
            <div key={deck.id} className="rounded-xl border border-border bg-card p-4 animate-fade-in">
              <div className="flex items-center gap-2 mb-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-sm">{deck.profile_emoji}</span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-sm font-semibold text-foreground truncate">{deck.name}</h3>
                  <span className="text-[10px] text-muted-foreground font-body">by {deck.profile_username}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <InkDot ink={deck.ink_1 as InkColor} size="md" />
                <InkDot ink={deck.ink_2 as InkColor} size="md" />
                <span className="text-xs text-muted-foreground font-body">{deck.ink_1} / {deck.ink_2}</span>
                <span className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground font-body">
                  {deck.total_cards}/60
                </span>
              </div>
              {deck.description && <p className="text-xs text-muted-foreground font-body mb-3">{deck.description}</p>}

              {/* Rating */}
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    onClick={() => user && handleRate(deck.id, star)}
                    className="transition-colors"
                  >
                    <Star className={`h-4 w-4 ${(deck.user_rating || 0) >= star ? 'fill-primary text-primary' : (deck.avg_rating || 0) >= star ? 'fill-primary/30 text-primary/30' : 'text-muted-foreground/30'}`} />
                  </button>
                ))}
                <span className="text-[10px] text-muted-foreground font-body ml-1">
                  {deck.avg_rating ? deck.avg_rating.toFixed(1) : '—'} ({deck.rating_count || 0})
                </span>
              </div>

              {/* Actions */}
              {user && deck.user_id !== user.id && (
                <button
                  onClick={() => handleCopy(deck.id)}
                  disabled={copyDeck.isPending}
                  className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-primary/10 py-2.5 text-xs font-medium text-primary font-body hover:bg-primary/20 transition-colors disabled:opacity-50"
                >
                  <Copy className="h-3.5 w-3.5" /> {copyDeck.isPending ? 'Copying...' : 'Copy to My Decks'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
